/* Services — the turn types this salon's board is built out of.
 *
 * Every column of `tt_services`, which is one more than the console's own page
 * shows: `sort_order` is a control here rather than a drag, and the shortcut and
 * abbreviation are editable side by side with the duplicate check the board
 * relies on.
 *
 * The one thing to know before editing this page: a service is what a turn
 * WEIGHS. Changing `turn_weight` does not re-weigh the turns already recorded —
 * they carry their own weight on the row, deliberately, so history does not
 * rewrite itself when a salon reclassifies a service. The change applies from
 * the next turn on.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { nav } from '../nav.js';
import { store } from '../store.js';
import { ConfirmButton } from '../components/confirm-button.js';
// The hotkey is optional and the square's label is not — see ../rules/services.js.
import { serviceAbbrev } from '../rules/services.js';

// The console's palette and its neutral, restated — see the note in
// js/rules/theme-presets.js about why these are copies.
var PALETTE = [
  '#C5A059', '#7A9E7E', '#C98B8B', '#6E86B8',
  '#A98BC4', '#5FA8A0', '#B98A5E', '#8C93A8'
];
var WEIGHTS = [0, 0.5, 1];
var MIN_MINUTES = 5;
var MAX_MINUTES = 240;

function weightLabel(w) {
  return w === 0 ? 'Zero' : (w === 0.5 ? 'Half' : 'Full');
}

var Services = {
  name: 'AdminServices',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var newName = Vue.ref('');
    var newKey = Vue.ref('');
    var showArchived = Vue.ref(false);

    var services = Vue.computed(function () {
      return store.data.services.filter(function (s) { return s.active !== false; });
    });

    var archived = Vue.computed(function () {
      return store.data.services.filter(function (s) { return s.active === false; });
    });

    // Two services on one key is not an error the database catches, and the
    // board simply answers the first — so the only place it can be noticed is a
    // screen that counts them. Keyless services are skipped: they all share the
    // empty string, and three services nobody presses is not a collision.
    var duplicateKeys = Vue.computed(function () {
      var seen = {}, dup = {};
      services.value.forEach(function (s) {
        var k = String(s.shortcut || '').toUpperCase();
        if (!k) return;
        if (seen[k]) dup[k] = true;
        seen[k] = true;
      });
      return dup;
    });

    async function patch(row, changes) {
      var id = store.selectedId.value;
      // The shape the column wants, not the shape the input gave. `shortcut` is
      // a single upper-case character or null by constraint —
      // `shortcut is null or char_length(shortcut) = 1` — so a paste of two
      // letters, and an emptied box, would both be refused by Postgres with a
      // constraint name rather than a sentence.
      var clean = Object.assign({}, changes);
      if ('shortcut' in clean) {
        // Emptying it is a real edit: the hotkey is optional, and clearing it
        // is how a service comes off the board's keyboard. Null rather than '',
        // which the length check would refuse.
        clean.shortcut = String(clean.shortcut || '').trim().toUpperCase().slice(0, 1) || null;
      }
      if ('abbrev' in clean) {
        clean.abbrev = String(clean.abbrev || '').trim().slice(0, 2) || null;
      }
      if ('default_minutes' in clean) {
        var n = Math.round(Number(clean.default_minutes));
        clean.default_minutes = (!isFinite(n) || n <= 0)
          ? null
          : Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, n));
      }

      if ('same_time' in clean) {
        // Feet, hands or off — see the console's js/rules/same-time.js.
        clean.same_time = clean.same_time === 'feet' || clean.same_time === 'hands'
          ? clean.same_time : null;
      }

      var res = await api.updateRow('tt_services', id, { id: row.id }, clean);
      if (!res) return;
      Object.assign(row, clean);
      api.log(id, 'service.update', { service: row.id, changes: clean });
    }

    async function add() {
      var name = newName.value.trim();
      var id = store.selectedId.value;
      if (!name) { api.toast('Enter a service name.', 'bad'); return; }
      // Optional, and no longer defaulted to the name's first letter: that
      // default was what made the hotkey compulsory, and it handed two services
      // beginning with the same letter a collision nobody chose.
      var key = newKey.value.trim().toUpperCase().slice(0, 1) || null;

      var row = {
        id: (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : undefined,
        name: name,
        shortcut: key,
        turn_weight: 1,
        color: PALETTE[store.data.services.length % PALETTE.length],
        sort_order: (store.data.services.length + 1) * 10,
        active: true,
        quick_add: true
      };
      var res = await api.writeRow('tt_services', id, row);
      if (!res) return;
      store.data.services.push(res[0] || row);
      newName.value = '';
      newKey.value = '';
      api.log(id, 'service.add', { name: name, shortcut: key });
    }

    async function destroy(row) {
      var id = store.selectedId.value;
      var res = await api.deleteRows('tt_services', id, { id: row.id });
      if (!res) return;
      store.data.services = store.data.services.filter(function (s) { return s.id !== row.id; });
      api.log(id, 'service.delete', { service: row.id, name: row.name });
      // Turns keep their service NAME on the row and lose only the link — the
      // foreign key is `on delete set null (service_id)`. History survives.
      api.toast('Deleted. Past turns keep the name they were recorded with.');
    }

    function move(row, delta) {
      var list = services.value;
      var i = list.indexOf(row);
      var j = i + delta;
      if (j < 0 || j >= list.length) return;
      var a = row.sort_order, b = list[j].sort_order;
      patch(row, { sort_order: b });
      patch(list[j], { sort_order: a });
    }

    return {
      services: services,
      archived: archived,
      showArchived: showArchived,
      duplicateKeys: duplicateKeys,
      newName: newName, newKey: newKey,
      add: add, patch: patch, destroy: destroy, move: move,
      letterFor: function (svc) {
        return serviceAbbrev(svc.abbrev, svc.name, svc.shortcut);
      },
      // What the square falls back to if the Abbrev box is emptied.
      fallbackLetter: function (svc) {
        return serviceAbbrev('', svc.name, svc.shortcut);
      },
      palette: PALETTE,
      weights: WEIGHTS,
      weightLabel: weightLabel,
      minMinutes: MIN_MINUTES,
      maxMinutes: MAX_MINUTES,
      loading: Vue.computed(function () { return store.data.loading; })
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Services</h2>',
    '    <p class="page__lead">The turn types. The <strong>key</strong> is what the desk',
    '      presses to book one, and it is optional &mdash; a salon that never uses the',
    '      keyboard leaves it empty. The <strong>abbreviation</strong> is what is drawn in',
    '      the square; left empty it follows the key, or the name where there is no key.',
    '      Each counts as a full, half or zero turn against a technician&rsquo;s place',
    '      in the queue.</p>',
    '  </div>',
    '',
    '  <form class="card" @submit.prevent="add">',
    '    <h3>Add a service</h3>',
    '    <div class="card__row">',
    '      <label class="field field--grow">',
    '        <span class="field__label">Service</span>',
    '        <input v-model="newName" class="input" type="text" maxlength="30"',
    '          placeholder="Service name" />',
    '      </label>',
    '      <label class="field">',
    '        <span class="field__label">Key</span>',
    /* A dash, not the name's first letter. The letter read as a suggestion and
       behaved as a default, which is what used to make the hotkey compulsory. */
    '        <input v-model="newKey" class="input input--key" type="text" maxlength="1"',
    '          placeholder="&mdash;" title="Hotkey &mdash; optional" />',
    '      </label>',
    '      <button type="submit" class="btn btn--primary">Add</button>',
    '    </div>',
    '    <p class="hint">The key is optional. Added as a full turn, on the next colour in the',
    '      palette, and offered in Next availability. All of that is editable below.</p>',
    '  </form>',
    '',
    '  <p v-if="loading" class="empty">Loading…</p>',
    '  <p v-else-if="!services.length" class="empty">This salon has no services yet, so its',
    '    board cannot start a turn at all.</p>',
    '',
    '  <ul class="rows">',
    '    <li v-for="(svc, i) in services" :key="svc.id" class="row">',
    '      <div class="row__order">',
    '        <button type="button" class="icon-btn" :disabled="i === 0" @click="move(svc, -1)"',
    '          aria-label="Move up">&#9650;</button>',
    '        <span class="row__pos">{{ i + 1 }}</span>',
    '        <button type="button" class="icon-btn" :disabled="i === services.length - 1"',
    '          @click="move(svc, 1)" aria-label="Move down">&#9660;</button>',
    '      </div>',
    '',
    '      <div class="row__main">',
    '        <div class="row__top">',
    '          <span class="swatch-letter" :style="{ background: svc.color }">',
    '            {{ letterFor(svc) }}</span>',
    '          <label class="field field--grow">',
    '            <span class="field__label">Name</span>',
    '            <input class="input" type="text" :value="svc.name" maxlength="30"',
    '              @change="patch(svc, { name: $event.target.value })" />',
    '          </label>',
    '          <label class="field">',
    '            <span class="field__label">Key</span>',
    '            <input class="input input--key" type="text" :value="svc.shortcut || \'\'" maxlength="1"',
    '              placeholder="&mdash;" title="Hotkey &mdash; optional"',
    '              :class="{ \'input--bad\': duplicateKeys[svc.shortcut] }"',
    '              @change="patch(svc, { shortcut: $event.target.value })" />',
    '          </label>',
    '          <label class="field">',
    '            <span class="field__label">Abbrev</span>',
    '            <input class="input input--key" type="text" :value="svc.abbrev || \'\'"',
    '              maxlength="2" :placeholder="fallbackLetter(svc)"',
    '              @change="patch(svc, { abbrev: $event.target.value })" />',
    '          </label>',
    '          <label class="field">',
    '            <span class="field__label">Minutes</span>',
    '            <input class="input input--mins" type="number" :min="minMinutes" :max="maxMinutes"',
    '              step="5" placeholder="—"',
    '              :value="svc.default_minutes == null ? \'\' : svc.default_minutes"',
    '              title="How long this usually takes. A booking\'s own length always wins."',
    '              @change="patch(svc, { default_minutes: $event.target.value })" />',
    '          </label>',
    '          <label class="field">',
    '            <span class="field__label">Same time</span>',
    '            <select class="input input--mins" :value="svc.same_time || \'\'"',
    '              title="On one visit a Feet service (pedicure) may be done at the same time as a Hands service (manicure, set, refill). Off never runs alongside anything."',
    '              @change="patch(svc, { same_time: $event.target.value })">',
    '              <option value="">Off</option>',
    '              <option value="feet">Feet</option>',
    '              <option value="hands">Hands</option>',
    '            </select>',
    '          </label>',
    '        </div>',
    '',
    '        <div class="row__top">',
    '          <div class="pick" role="radiogroup" :aria-label="svc.name + \' counts as\'">',
    '            <button v-for="w in weights" :key="w" type="button" class="pick__opt"',
    '              :class="{ \'is-on\': Number(svc.turn_weight) === w }" role="radio"',
    '              :aria-checked="Number(svc.turn_weight) === w ? \'true\' : \'false\'"',
    '              @click="patch(svc, { turn_weight: w })">{{ weightLabel(w) }}</button>',
    '          </div>',
    '          <label class="switch switch--sm">',
    '            <input type="checkbox" :checked="svc.quick_add !== false"',
    '              @change="patch(svc, { quick_add: $event.target.checked })" />',
    '            <span class="switch__track"><span class="switch__thumb"></span></span>',
    '            <span class="switch__label">Next availability</span>',
    '          </label>',
    '          <div class="swatches">',
    '            <button v-for="c in palette" :key="c" type="button" class="swatch"',
    '              :class="{ \'is-on\': svc.color === c }" :style="{ background: c }"',
    '              :aria-label="\'Set colour \' + c" @click="patch(svc, { color: c })"></button>',
    '            <input type="color" class="swatch swatch--pick" :value="svc.color"',
    '              title="Any colour — the palette is a suggestion, not a constraint"',
    '              @change="patch(svc, { color: $event.target.value })" />',
    '          </div>',
    '        </div>',
    '',
    '        <p v-if="duplicateKeys[svc.shortcut]" class="warn-line">',
    '          Key <strong>{{ svc.shortcut }}</strong> is used more than once &mdash; only the',
    '          first service responds to it on the board.</p>',
    '      </div>',
    '',
    '      <div class="row__end">',
    '        <confirm-button label="Archive" confirm="Archive (keeps history)" size="sm"',
    '          kind="quiet" @confirmed="patch(svc, { active: false })" />',
    '        <confirm-button label="Delete" confirm="Delete (turns keep the name)" size="sm"',
    '          @confirmed="destroy(svc)" />',
    '      </div>',
    '    </li>',
    '  </ul>',
    '',
    '  <div v-if="archived.length" class="card">',
    '    <button type="button" class="link-btn" @click="showArchived = !showArchived">',
    '      {{ showArchived ? "Hide" : "Show" }} archived services ({{ archived.length }})</button>',
    '    <ul v-if="showArchived" class="rows rows--tight">',
    '      <li v-for="svc in archived" :key="svc.id" class="row row--slim">',
    '        <span class="row__name">{{ svc.name }}</span>',
    '        <span class="row__end">',
    '          <button type="button" class="btn btn--quiet btn--sm"',
    '            @click="patch(svc, { active: true })">Restore</button>',
    '          <confirm-button label="Delete" confirm="Delete (turns keep the name)" size="sm"',
    '            @confirmed="destroy(svc)" />',
    '        </span>',
    '      </li>',
    '    </ul>',
    '  </div>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'services',
  name: 'Services',
  group: 'Console settings',
  page: Services
});

export { Services };
